import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, BookOpen, PlayCircle, ArrowLeft, Loader2 } from 'lucide-react';
import { MembershipTier } from '../types/membership';

interface StorySelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onStartReading: (bookId?: string, bookTitle?: string) => void | Promise<void>; // Updated to accept book identity, can be async
  title: string;
  content: string;
  wordCount: number;
  readingTimeMinutes: number;
  difficulty: string;
  source?: string;
  spoilerFreeSummary?: string;
  userTier?: MembershipTier;
  bookId?: string; // NEW: pass book identifier
  bookTitle?: string; // NEW: pass book title
}

export const StorySelectionModal: React.FC<StorySelectionModalProps> = ({
  isOpen,
  onClose,
  onStartReading,
  title,
  content,
  wordCount,
  readingTimeMinutes,
  difficulty,
  source,
  spoilerFreeSummary,
  userTier,
  bookId,
  bookTitle
}) => {
  const navigate = useNavigate();
  const modalRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  
  // Debug log
  console.log('StorySelectionModal - userTier:', userTier);
  
  // Check if userTier is guest or undefined - treat undefined as guest (unauthenticated)
  const isGuestUser = userTier === 'guest' || userTier === undefined;

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Focus trap and click outside to close
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
      onClose();
    }
  };

  // Calculate reading time at different WPM speeds
  const getReadingTimeAtWpm = (wordCount: number, wpm: number): string => {
    const totalSeconds = Math.round((wordCount / wpm) * 60);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    if (minutes === 0) return `${seconds}s`;
    if (seconds === 0) return `${minutes}m`;
    return `${minutes}m ${seconds}s`;
  };

  if (!isOpen) return null;

  // Generate a preview/summary of the content
  const getPreview = (text: string, maxLength: number = 300): string => {
    const cleaned = text.replace(/\s+/g, ' ').trim();
    if (cleaned.length <= maxLength) return cleaned;
    return cleaned.substring(0, maxLength) + '...';
  };

  const getDifficultyColor = (diff: string) => {
    switch (diff) {
      case 'beginner':
        return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
      case 'intermediate':
        return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'advanced':
        return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
      default:
        return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={handleBackdropClick}
    >
      <div 
        ref={modalRef}
        className="w-full max-w-lg bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 animate-in zoom-in-95 duration-200"
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700">
          <h2 id="modal-title" className="text-xl font-semibold text-slate-900 dark:text-white">
            {title}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
            aria-label="Close modal"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Meta info */}
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${getDifficultyColor(difficulty)}`}>
              {difficulty}
            </span>
            <span className="text-sm text-slate-500 dark:text-slate-400">
              {wordCount.toLocaleString()} words
            </span>
            <span className="text-slate-300 dark:text-slate-600">•</span>
            <span className="text-sm text-slate-500 dark:text-slate-400">
              @200: {getReadingTimeAtWpm(wordCount, 200)}
            </span>
            <span className="text-slate-300 dark:text-slate-600">•</span>
            <span className="text-sm text-slate-500 dark:text-slate-400">
              @500: {getReadingTimeAtWpm(wordCount, 500)}
            </span>
            {source && (
              <>
                <span className="text-slate-300 dark:text-slate-600">•</span>
                <span className="text-sm text-slate-500 dark:text-slate-400">
                  {source}
                </span>
              </>
            )}
          </div>

          {/* Summary/Preview */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-2 uppercase tracking-wide">
              Summary
            </h3>
            <p className="text-slate-700 dark:text-slate-300 leading-relaxed">
              {spoilerFreeSummary || getPreview(content)}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="p-6 pt-0 flex flex-col sm:flex-row gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-xl font-medium hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors flex items-center justify-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to List
          </button>
          <button
            onClick={() => {
              if (isLoading) return;
              
              console.log('Button clicked, userTier:', userTier, 'isGuestUser:', isGuestUser);
              // Let guests read, but check session limit first
              if (isGuestUser) {
                // Backward compatibility: check both old and new keys
                const oldKey = parseInt(localStorage.getItem('flashread_guest_sessions') || '0', 10);
                const newKey = parseInt(localStorage.getItem('flashread_free_stories_used') || '0', 10);
                const freeStoriesUsed = Math.max(oldKey, newKey);
                if (freeStoriesUsed >= 3) {
                  // Session limit reached, redirect to upsell
                  navigate('/upsell');
                  onClose();
                  return;
                }
              }
              // Guest has sessions remaining, or user is authenticated — start reading
              setIsLoading(true);
              const result = onStartReading(bookId, bookTitle);
              if (result && typeof result === 'object' && 'finally' in result) {
                result.finally(() => setIsLoading(false));
              } else {
                setIsLoading(false);
              }
            }}
            disabled={isLoading}
            className={`flex-1 px-4 py-3 rounded-xl font-medium shadow-lg flex items-center justify-center gap-2 transition-all disabled:opacity-70 disabled:cursor-not-allowed ${
              isGuestUser
                ? 'bg-green-500 hover:bg-green-600 text-white shadow-green-200 dark:shadow-green-900/20'
                : 'bg-blue-600 hover:bg-blue-700 text-white shadow-blue-200 dark:shadow-blue-900/20'
            }`}
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <PlayCircle className="h-4 w-4" />
            )}
            {isLoading ? 'Loading...' : (isGuestUser ? 'Start Reading (Free)' : 'Start Reading')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StorySelectionModal;
